import React, { useEffect, useState } from 'react';
import Actions from '../flux/Actions';
import ProjectStore from '../flux/stores/ProjectStore';
import type { Character, Location, Plot, Scene } from '../types/entities';
import EntityDetail from './entities/EntityDetail';

interface ContentAreaProps {
   activeTab: string;
}

interface EntitySnapshot {
   scenes: Scene[];
   selectedSceneId: string | null;
   characters: Character[];
   selectedCharacterId: string | null;
   locations: Location[];
   selectedLocationId: string | null;
   plots: Plot[];
   selectedPlotId: string | null;
}

const getSnapshot = (): EntitySnapshot => ({
   scenes: ProjectStore.getScenes(),
   selectedSceneId: ProjectStore.getSelectedSceneId(),
   characters: ProjectStore.getCharacters(),
   selectedCharacterId: ProjectStore.getSelectedCharacterId(),
   locations: ProjectStore.getLocations(),
   selectedLocationId: ProjectStore.getSelectedLocationId(),
   plots: ProjectStore.getPlots(),
   selectedPlotId: ProjectStore.getSelectedPlotId(),
});

const emptyStateCopy = {
   scenes: {
      title: 'No scene selected',
      body: 'Choose a scene in the sidebar or create a new one.',
      button: 'New Scene',
      onCreate: () => Actions.showModal('newScene'),
   },
   plots: {
      title: 'No plot selected',
      body: 'Choose a plot in the sidebar or create a new one.',
      button: 'New Plot',
      onCreate: () => Actions.showModal('newPlot'),
   },
   characters: {
      title: 'No character selected',
      body: 'Choose a character in the sidebar or create a new one.',
      button: 'New Character',
      onCreate: () => Actions.showModal('newCharacter'),
   },
   locations: {
      title: 'No location selected',
      body: 'Choose a location in the sidebar or create a new one.',
      button: 'New Location',
      onCreate: () => Actions.showModal('newLocation'),
   },
};

function EmptyEntityState({ activeTab }: { activeTab: string }) {
   const copy = emptyStateCopy[activeTab as keyof typeof emptyStateCopy] || emptyStateCopy.scenes;

   return (
      <div className="h-full flex items-center justify-center px-8">
         <div className="max-w-sm text-center">
            <h3 className="text-2xl font-display text-silver mb-2 uppercase">{copy.title}</h3>
            <p className="text-ash font-mono text-sm mb-5">{copy.body}</p>
            <button
               type="button"
               onClick={copy.onCreate}
               className="inline-flex items-center justify-center gap-2 rounded-md bg-charcoal px-4 py-2 text-sm font-mono text-silver hover:bg-slate hover:text-pearl transition-colors"
            >
               <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                     strokeLinecap="round"
                     strokeLinejoin="round"
                     strokeWidth={2}
                     d="M12 4v16m8-8H4"
                  />
               </svg>
               {copy.button}
            </button>
         </div>
      </div>
   );
}

function ContentArea({ activeTab }: ContentAreaProps) {
   const [snapshot, setSnapshot] = useState<EntitySnapshot>(getSnapshot);

   useEffect(() => {
      const handleProjectChange = () => setSnapshot(getSnapshot());

      ProjectStore.addChangeListener(handleProjectChange);
      handleProjectChange();

      return () => ProjectStore.removeChangeListener(handleProjectChange);
   }, []);

   const selectedScene = snapshot.scenes.find((scene) => scene.id === snapshot.selectedSceneId);
   const selectedCharacter = snapshot.characters.find(
      (character) => character.id === snapshot.selectedCharacterId,
   );
   const selectedLocation = snapshot.locations.find(
      (location) => location.id === snapshot.selectedLocationId,
   );
   const selectedPlot = snapshot.plots.find((plot) => plot.id === snapshot.selectedPlotId);

   const renderContent = () => {
      switch (activeTab) {
         case 'plots':
            return selectedPlot ? (
               <EntityDetail
                  entity={selectedPlot}
                  entityType="plot"
                  onEdit={() => Actions.showModal('editPlot', { plotId: selectedPlot.id })}
                  onDelete={() => {
                     if (
                        selectedPlot.id &&
                        window.confirm('Are you sure you want to delete this plot point?')
                     ) {
                        Actions.deletePlot(selectedPlot.id);
                     }
                  }}
               />
            ) : (
               <EmptyEntityState activeTab="plots" />
            );
         case 'characters':
            return selectedCharacter ? (
               <EntityDetail
                  entity={selectedCharacter}
                  entityType="character"
                  onEdit={() =>
                     Actions.showModal('editCharacter', { characterId: selectedCharacter.id })
                  }
                  onDelete={() => {
                     if (
                        selectedCharacter.id &&
                        window.confirm('Are you sure you want to delete this character?')
                     ) {
                        Actions.deleteCharacter(selectedCharacter.id);
                     }
                  }}
               />
            ) : (
               <EmptyEntityState activeTab="characters" />
            );
         case 'locations':
            return selectedLocation ? (
               <EntityDetail
                  entity={selectedLocation}
                  entityType="location"
                  onEdit={() =>
                     Actions.showModal('editLocation', { locationId: selectedLocation.id })
                  }
                  onDelete={() => {
                     if (
                        selectedLocation.id &&
                        window.confirm('Are you sure you want to delete this location?')
                     ) {
                        Actions.deleteLocation(selectedLocation.id);
                     }
                  }}
               />
            ) : (
               <EmptyEntityState activeTab="locations" />
            );
         case 'scenes':
         default:
            return selectedScene ? (
               <EntityDetail
                  entity={selectedScene}
                  entityType="scene"
                  onEdit={() => Actions.showModal('editScene', { sceneId: selectedScene.id })}
                  onDelete={() => {
                     if (
                        selectedScene.id &&
                        window.confirm('Are you sure you want to delete this scene?')
                     ) {
                        Actions.deleteScene(selectedScene.id);
                     }
                  }}
               />
            ) : (
               <EmptyEntityState activeTab="scenes" />
            );
      }
   };

   return <main className="min-w-0 flex-1 bg-midnight overflow-hidden">{renderContent()}</main>;
}

export default ContentArea;
